import React, { useState, useEffect, useCallback } from "react";
import { Users, History } from "lucide-react";
import { EditCustomerPackageModal } from "../../components/sunbeds/EditCustomerPackageModal";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { CustomerPackagesTable, CustomerPackage } from "../../components/sunbeds/CustomerPackagesTable";
import { CustomerPackageLogs } from "../../components/sunbeds/CustomerPackageLogs";
import {
  getSunbedCustomerPackages,
  SunbedCustomerPackageApi,
  updateSunbedCustomerPackage,
  parseSunbedMinutes,
  ApiError,
} from "../../services/sunbeds";
import { useToast } from "../../hooks/useToast";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";

const CustomerPackages: React.FC = () => {
  const toast = useToast();
  const [packages, setPackages] = useState<CustomerPackage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [editingPackage, setEditingPackage] = useState<CustomerPackage | null>(
    null
  );
  const [showLogs, setShowLogs] = useState<boolean>(false);

  const fetchCustomerPackages = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getSunbedCustomerPackages();

      const apiData: SunbedCustomerPackageApi[] = res.data || [];

      // Map API result into the table view model
      const mapped: CustomerPackage[] = apiData.map((pkg) => ({
        id: String(pkg.id),
        customerName: pkg.user?.name?.trim() || "Unknown",
        customerPhone:
          String(
            pkg.user?.formatted_mobile ||
              pkg.user?.mobile ||
              pkg.user?.contact_number ||
              "",
          ).trim() || "—",
        customerEmail: String(pkg.user?.email ?? "").trim() || "—",
        packageName: pkg.package_name || pkg.sunbed?.label || "Sunbed Package",
        usedMinutes: parseSunbedMinutes(pkg.used_minutes),
        remainingMinutes: parseSunbedMinutes(pkg.remaining_balance),
        sunbedId: pkg.sunbed_id,
      }));

      setPackages(mapped);
    } catch (error) {
      console.error("Failed to load customer packages from /sunbed-bookings", error);
      toast.error("Failed to load customer packages. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load from /sunbed-bookings
  useEffect(() => {
    fetchCustomerPackages();
  }, [fetchCustomerPackages]);

  const filteredPackages = packages.filter((p) => {
    const term = searchTerm.toLowerCase();
    const matches = (value?: string | null) =>
      String(value ?? "").toLowerCase().includes(term);

    return (
      matches(p.customerName) ||
      matches(p.customerPhone) ||
      matches(p.customerEmail) ||
      matches(p.packageName)
    );
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedPackages = filteredPackages.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleUpdate = async (newMinutes: number, sunbedId: number): Promise<void> => {
    if (!editingPackage) {
      throw new Error("No package selected for editing");
    }

    // Extra client-side guard (should already be enforced in the modal)
    if (newMinutes > editingPackage.remainingMinutes) {
      const errorMessage = `New remaining minutes (${newMinutes}) cannot be greater than the current balance (${editingPackage.remainingMinutes}).`;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    try {
      const response = await updateSunbedCustomerPackage(
        editingPackage.id,
        newMinutes,
        sunbedId
      );

      // Show success message from API response
      const successMessage = response.message || "Package updated successfully.";
      toast.success(successMessage);

      // Refresh list from server to reflect latest values
      await fetchCustomerPackages();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      let errorMessage = "Failed to update customer package.";
      
      if (apiErr.type === "server" || apiErr.type === "network") {
        errorMessage = apiErr.message || errorMessage;
      } else if (apiErr.type === "serverValidation") {
        // Handle validation errors
        const errors = apiErr.errors;
        if (errors) {
          errorMessage = Object.values(errors).join(", ");
        } else {
          errorMessage = "Validation error. Please check your input.";
        }
      }
      
      toast.error(errorMessage);
      throw err; // Re-throw to let the modal handle the error state
    }
  };

  return (
    <div>
      <div className="px-6">
        <div className="flex items-center justify-between px-6 py-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-[#F3E8FF] p-3 rounded-xl">
              {showLogs ? (
                <History className="h-6 w-6 text-[#885ABB]" />
              ) : (
                <Users className="h-6 w-6 text-[#885ABB]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {showLogs ? "Package Logs" : "Customer Packages"}
              </h1>
              <p className="text-sm text-gray-500 font-medium">
                {showLogs
                  ? "View all customer package update history and activity logs"
                  : "View and manage customer package usage and remaining balances"}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLogs(!showLogs)}
              className={cn(
                "gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                !showLogs && "bg-gray-100 text-gray-900"
              )}
            >
              {showLogs ? <Users size={16} /> : <History size={16} />}
              {showLogs ? "Packages" : "View Logs"}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">

        {showLogs ? (
          <div className="flex-1 overflow-hidden p-6">
            <CustomerPackageLogs />
          </div>
        ) : (
          <div className="flex-1 flex flex-col p-6 space-y-6 overflow-hidden">
            {/* Action Toolbar */}
            <SunbedActionToolbar
              totalItems={packages.length}
              showingStart={paginatedPackages.length > 0 ? indexOfFirstItem + 1 : 0}
              showingEnd={Math.min(indexOfLastItem, filteredPackages.length)}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search customers..."
            />

            {/* Table Section */}
            <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
              <CustomerPackagesTable
                packages={paginatedPackages}
                indexOfFirstItem={indexOfFirstItem}
                onEdit={setEditingPackage}
                isLoading={isLoading}
              />

              {/* Pagination Footer */}
              <PaginationFooter
                totalItems={packages.length}
                filteredTotal={filteredPackages.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
          </div>
        )}
      </div>

      <EditCustomerPackageModal
        isOpen={!!editingPackage}
        onClose={() => setEditingPackage(null)}
        onUpdate={handleUpdate}
        currentMinutes={editingPackage?.remainingMinutes || 0}
        currentSunbedId={editingPackage?.sunbedId}
      />
    </div>
  );
};
export default CustomerPackages;
